import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  Typography
} from '@mui/material';
import React, { forwardRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import DefaultModal from '../../components/Modals/DefaultModal';
import SuccessModal from '../../components/Modals/SuccessModal';
import { userService } from '../../services/userService';

const PasswordField = forwardRef((props: TextFieldProps, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <TextField
      sx={{ width: '100%', mb: 3 }}
      type={showPassword ? 'text' : 'password'}
      inputRef={ref}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword((state) => !state)}>
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
      {...props}
    />
  );
});

PasswordField.displayName = 'PasswordField';

interface Props {
  openModal: boolean;
  closeModal: () => void;
}

const schema = yup.object({
  newPassword: yup
    .string()
    .default('')
    .required('New passwords required')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character'
    ),
  confirmPassword: yup
    .string()
    .default('')
    .oneOf([yup.ref('newPassword'), null, ''], 'Passwords must match')
});

const defaultValues = schema.getDefault();

type ChangePasswordForm = yup.InferType<typeof schema>;

const ChangePassword = (props: Props): JSX.Element => {
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);
  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ChangePasswordForm>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  const handleChangePassword = () => {
    if (newPassword === confirmPassword) {
      userService.updateUserPassword(confirmPassword).then(() => {
        handleCloseModal();
        setConfirmDialog(true);
      });
    }
  };

  const handleCloseModal = () => {
    reset();
    props.closeModal();
  };

  return (
    <>
      <DefaultModal open={props.openModal} handleClose={handleCloseModal}>
        <form onSubmit={handleSubmit(handleChangePassword)}>
          <Box sx={{ px: 8 }}>
            <Typography variant="h4" textAlign="center" color="grey.900">
              Change Password
            </Typography>
            <Typography textAlign="center" my={4}>
              Choose a unique password to protect your account. It must be
              different from previous used password.
            </Typography>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <PasswordField
                  {...field}
                  error={!!errors.newPassword}
                  helperText={
                    errors.newPassword ? errors.newPassword?.message : ''
                  }
                  label="New Password"
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <PasswordField
                  {...field}
                  error={!!errors.confirmPassword}
                  helperText={
                    errors.confirmPassword
                      ? errors.confirmPassword?.message
                      : ''
                  }
                  label="Confirm New Password"
                />
              )}
            />
            <ul style={{ paddingLeft: '20px' }}>
              <li>At least 8 characters</li>
              <li>At least one upper case letter (A-Z)</li>
              <li>At least one number (0-9)</li>
              <li>At least one of the following: @#!$%*</li>
            </ul>
          </Box>
          <DialogActions sx={{ justifyContent: 'center', py: 3 }}>
            <Button onClick={handleCloseModal} variant="outlined">
              Cancel
            </Button>
            <Button type="submit">Change Password</Button>
          </DialogActions>
        </form>
      </DefaultModal>

      <SuccessModal
        open={confirmDialog}
        handleClose={() => setConfirmDialog(false)}
        message="Your password has been changed successfully."
      />
    </>
  );
};

export default ChangePassword;
